import { forwardRef, isValidElement } from "react"
import classNames from "classnames"
import { Heading, Text } from ".."
import { useCardContext } from "./CardContext"
import { devWarning } from "../utils/devWarning"
import type { CardHeaderProps } from "./types"

export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  (
    { subtitle, children, startAdornment, endAdornment, className: classNameProp, ...rest },
    ref,
  ) => {
    const { size: cardSize } = useCardContext()

    const hideTitleAndSubtitle = cardSize === "none" || cardSize === "xxs"

    devWarning(
      hideTitleAndSubtitle,
      `Card title and subtitle are not displayed when the size is: ${cardSize}`,
    )

    const _startAdornment = isValidElement(startAdornment) && startAdornment
    const _endAdornment = isValidElement(endAdornment) && endAdornment

    const titleAndSubtitleWrapperClassName = classNames("grow", {
      "ml-4": _startAdornment,
      "mr-2": _endAdornment,
    })

    const _title =
      typeof children === "string" ? (
        <Heading as="h2" size="lg">
          {children}
        </Heading>
      ) : (
        children
      )
    const _subtitle =
      typeof subtitle === "string" ? (
        <Text className="text-sm leading-4 text-gray-400">{subtitle}</Text>
      ) : (
        subtitle
      )

    return (
      <div ref={ref} className={classNames(classNameProp, "flex items-center")} {...rest}>
        {_startAdornment}

        <div className={titleAndSubtitleWrapperClassName}>
          {!hideTitleAndSubtitle && (
            <>
              {_title}
              {_subtitle}
            </>
          )}
        </div>

        {_endAdornment}
      </div>
    )
  },
)

CardHeader.displayName = "Card.Header"
